import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import api from "../../../../../api/axios";
import { companyID } from "../../../../../auth";
import config from "../../../../../config";

const CandidateModal = ({
  show,
  onClose,
  candidate = null, // null for new, object for edit
  onSave,
  submitting = false,
}) => {
  const { t } = useTranslation();
  const companyId = companyID();
  const isEdit = !!candidate;

  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    alternate_phone: "",
    gender: "MALE",
    dob: "",
    experience: "",
    current_ctc: "",
    expected_ctc: "",
    notice_period_days: "",
    department_id: "",
    designation_id: "",
    resume: null,
  });

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [errors, setErrors] = useState({});

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get(`department/${companyId}`);

        if (response?.data?.success && response.data.result) {
          setDepartments(response.data.result || []);
        } else if (response?.data && Array.isArray(response.data)) {
          setDepartments(response.data);
        } else {
          setDepartments([]);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        console.error("Error details:", error.response?.data);
        setDepartments([]);
      }
    };

    if (show && companyId) {
      fetchDepartments();
    }
  }, [companyId, show]);

  // Fetch designations when department is selected
  useEffect(() => {
    const fetchDesignations = async () => {
      if (selectedDepartment) {
        try {
          const response = await api.get(
            `department/designation/${selectedDepartment}`
          );

          if (response?.data?.success && response.data.result) {
            setDesignations(response.data.result || []);
          } else if (response?.data && Array.isArray(response.data)) {
            setDesignations(response.data);
          } else {
            setDesignations([]);
          }
        } catch (error) {
          console.error("Error fetching designations:", error);
          console.error("Error details:", error.response?.data);
          setDesignations([]);
        }
      } else {
        setDesignations([]);
      }
    };
    fetchDesignations();
  }, [selectedDepartment]);

  // Populate form data for edit mode
  useEffect(() => {
    if (show) {
      if (isEdit && candidate) {
        // Split full_name for edit mode
        const nameParts = (candidate.full_name || "").split(" ");
        const firstName = nameParts[0] || "";
        const lastName =
          nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
        const middleName =
          nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";

        setFormData({
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          email: candidate.email || "",
          phone: candidate.phone || "",
          alternate_phone: candidate.alternate_phone || "",
          gender: candidate.gender || "MALE",
          dob: candidate.dob
            ? new Date(candidate.dob).toISOString().split("T")[0]
            : "",
          experience: candidate.experience_years || "",
          current_ctc: candidate.current_ctc || "",
          expected_ctc: candidate.expected_ctc || "",
          notice_period_days: candidate.notice_period_days || "",
          department_id: candidate.department_id || "",
          designation_id: candidate.designation_id || "",
          resume: null,
        });
        setSelectedDepartment(candidate.department_id || "");
      } else {
        // Reset form for new candidate
        setFormData({
          first_name: "",
          middle_name: "",
          last_name: "",
          email: "",
          phone: "",
          alternate_phone: "",
          gender: "MALE",
          dob: "",
          experience: "",
          current_ctc: "",
          expected_ctc: "",
          notice_period_days: "",
          department_id: "",
          designation_id: "",
          resume: null,
        });
        setSelectedDepartment("");
      }
      setErrors({});
    }
  }, [candidate, show, isEdit]);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0] || null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Handle department change
      if (name === "department_id") {
        setSelectedDepartment(value);
        setFormData((prev) => ({
          ...prev,
          designation_id: "", // Reset designation when department changes
        }));
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      resume: null,
    }));
    // Reset file input
    const fileInput = document.querySelector('input[name="resume"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = t("First name is required");
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = t("Last name is required");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("Email is required");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("Email is invalid");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("Phone is required");
    }

    if (!formData.experience.trim()) {
      newErrors.experience = t("Experience is required");
    } else {
      // Validate experience against date of birth
      if (formData.dob) {
        const birthDate = new Date(formData.dob);
        const currentDate = new Date();
        const age = Math.floor((currentDate - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
        
        if (Number(formData.experience) > age) {
          newErrors.experience = t(`Experience cannot exceed ${age} years based on date of birth`);
        }
      }
    }

    if (!formData.department_id) {
      newErrors.department_id = t("Department is required");
    }

    if (!formData.designation_id) {
      newErrors.designation_id = t("Designation is required");
    }

    // For new candidates, resume is required
    if (!isEdit && !formData.resume) {
      newErrors.resume = t("Resume is required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare payload based on mode
    let payload;
    if (isEdit) {
      // For edit mode, use existing field names
      payload = {
        full_name:
          `${formData.first_name} ${formData.middle_name} ${formData.last_name}`
            .replace(/\s+/g, " ")
            .trim(),
        email: formData.email,
        phone: formData.phone,
        alternate_phone: formData.alternate_phone || null,
        gender: formData.gender,
        dob: formData.dob || null,
        experience_years: formData.experience
          ? Number(formData.experience)
          : null,
        current_ctc: formData.current_ctc ? Number(formData.current_ctc) : null,
        expected_ctc: formData.expected_ctc
          ? Number(formData.expected_ctc)
          : null,
        notice_period_days: formData.notice_period_days
          ? Number(formData.notice_period_days)
          : null,
        department_id: formData.department_id
          ? Number(formData.department_id)
          : null,
        designation_id: formData.designation_id
          ? Number(formData.designation_id)
          : null,
      };

      // Only include resume if a new file is selected
      if (formData.resume) {
        payload.resume = formData.resume;
      }
    } else {
      // For new registration, use the format expected by registerCandidate
      payload = {
        first_name: formData.first_name,
        middle_name: formData.middle_name || "",
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        alternate_phone: formData.alternate_phone || "",
        gender: formData.gender,
        dob: formData.dob || null,
        experience: formData.experience ? Number(formData.experience) : null,
        current_ctc: formData.current_ctc ? Number(formData.current_ctc) : null,
        expected_ctc: formData.expected_ctc
          ? Number(formData.expected_ctc)
          : null,
        notice_period_days: formData.notice_period_days
          ? Number(formData.notice_period_days)
          : null,
        department_id: formData.department_id
          ? Number(formData.department_id)
          : null,
        designation_id: formData.designation_id
          ? Number(formData.designation_id)
          : null,
        resume: formData.resume,
      };
    }

    onSave(payload);
  };

  const handleClose = () => {
    setFormData({
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      phone: "",
      alternate_phone: "",
      gender: "MALE",
      dob: "",
      experience: "",
      current_ctc: "",
      expected_ctc: "",
      notice_period_days: "",
      department_id: "",
      designation_id: "",
      resume: null,
    });
    setErrors({});
    setSelectedDepartment("");
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`fa fa-${isEdit ? "edit" : "plus"} me-2`}></i>
          {isEdit ? t("Edit Candidate") : t("Register New Candidate")}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t("First Name")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.first_name}
                  placeholder={t("Enter first name")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.first_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>{t("Middle Name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleInputChange}
                  placeholder={t("Enter middle name")}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t("Last Name")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.last_name}
                  placeholder={t("Enter last name")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.last_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t("Email")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  isInvalid={!!errors.email}
                  placeholder={t("Enter email address")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t("Phone")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  isInvalid={!!errors.phone}
                  placeholder={t("Enter phone number")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Alternate Phone")}</Form.Label>
                <Form.Control
                  type="tel"
                  name="alternate_phone"
                  value={formData.alternate_phone}
                  onChange={handleInputChange}
                  placeholder={t("Enter alternate phone")}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Gender")}</Form.Label>
                <Form.Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="MALE">{t("Male")}</option>
                  <option value="FEMALE">{t("Female")}</option>
                  <option value="OTHER">{t("Other")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Date of Birth")}</Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t("Experience (Years)")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  isInvalid={!!errors.experience}
                  min="0"
                  step="0.1"
                  placeholder="0.0"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.experience}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t("Department")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.department_id}
                >
                  <option value="">{t("Select Department")}</option>
                  {departments && departments.length > 0 ? (
                    departments.map((dept) => (
                      <option
                        key={dept.id || dept._id}
                        value={dept.id || dept._id}
                      >
                        {dept.department ||
                          dept.name ||
                          dept.department_name ||
                          "Unknown Department"}
                      </option>
                    ))
                  ) : (
                    <option disabled>{t("No departments available")}</option>
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.department_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  {t("Designation")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="designation_id"
                  value={formData.designation_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.designation_id}
                  disabled={!selectedDepartment}
                >
                  <option value="">{t("Select Designation")}</option>
                  {designations && designations.length > 0 ? (
                    designations.map((desig) => (
                      <option
                        key={desig.id || desig._id}
                        value={desig.id || desig._id}
                      >
                        {desig.designation ||
                          desig.name ||
                          desig.designation_name ||
                          "Unknown Designation"}
                      </option>
                    ))
                  ) : selectedDepartment ? (
                    <option disabled>{t("No designations available")}</option>
                  ) : (
                    <option disabled>{t("Select department first")}</option>
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.designation_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Notice Period (Days)")}</Form.Label>
                <Form.Control
                  type="number"
                  name="notice_period_days"
                  value={formData.notice_period_days}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="0"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Current CTC")}</Form.Label>
                <Form.Control
                  type="number"
                  name="current_ctc"
                  value={formData.current_ctc}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Expected CTC")}</Form.Label>
                <Form.Control
                  type="number"
                  name="expected_ctc"
                  value={formData.expected_ctc}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  {t("Resume")}
                  {!isEdit && <span className="text-danger">*</span>}
                </Form.Label>

                {isEdit && candidate?.resume_url && !formData.resume && (
                  <div className="mb-2 p-2 bg-light rounded border">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <i className="fa fa-file-pdf-o me-2 text-danger"></i>
                        <span className="text-muted small">
                          {t("Current Resume")}: {candidate.resume_url}
                        </span>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          const url = candidate.resume_url.startsWith("http")
                            ? candidate.resume_url
                            : `${config.IMAGE_URL}${candidate.resume_url}`;
                          window.open(url, "_blank");
                        }}
                      >
                        <i className="fa fa-eye me-1"></i>
                        {t("View")}
                      </Button>
                    </div>
                  </div>
                )}

                {formData.resume && (
                  <div className="mb-2 p-2 bg-opacity-10 rounded border ">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <i className="fa fa-file me-2 text-primary"></i>
                        <span className="text-primary small">
                          {t("Selected")}: {formData.resume.name}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={handleRemoveFile}
                      >
                        <i className="fa fa-times"></i>
                      </Button>
                    </div>
                  </div>
                )}

                <Form.Control
                  type="file"
                  name="resume"
                  onChange={handleInputChange}
                  isInvalid={!!errors.resume}
                  accept=".pdf,.doc,.docx"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.resume}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {isEdit
                    ? t(
                        "Upload new resume (PDF, DOC, DOCX). Leave empty to keep current resume."
                      )
                    : t("Upload resume (PDF, DOC, DOCX).")}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("Cancel")}
          </Button>

          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <i className="fa fa-spinner fa-spin me-2" />
            ) : (
              <i className={`fa fa-${isEdit ? "save" : "plus"} me-2`} />
            )}
            {isEdit ? t("Update Candidate") : t("Register Candidate")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CandidateModal;
